"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type QuotePrefill = {
  category?: string | undefined;
  audience?: string | undefined;
  occasion?: string | undefined;
  quantity?: string | undefined;
  notes?: string | undefined;
};

type Ctx = {
  prefill: QuotePrefill;
  applyPrefill: (p: QuotePrefill) => void;
  goToQuote: (p?: QuotePrefill) => void;
};

const QuoteContext = createContext<Ctx | null>(null);

export function QuoteProvider({ children }: { children: ReactNode }) {
  const [prefill, setPrefill] = useState<QuotePrefill>({});

  const applyPrefill = useCallback((p: QuotePrefill) => {
    setPrefill((prev) => ({ ...prev, ...p }));
  }, []);

  const goToQuote = useCallback(
    (p?: QuotePrefill) => {
      if (p) applyPrefill(p);
      window.dispatchEvent(new Event("mills:need-quote"));
      const el = document.getElementById("quote");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [applyPrefill],
  );

  const value = useMemo(
    () => ({ prefill, applyPrefill, goToQuote }),
    [prefill, applyPrefill, goToQuote],
  );

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
}

export function useQuote() {
  const ctx = useContext(QuoteContext);
  if (!ctx) throw new Error("useQuote must be used within QuoteProvider");
  return ctx;
}

export const CATEGORIES = [
  "Branded Promotional Products",
  "Corporate and Employee Gifts",
  "Awards and Recognition",
  "Event Merchandise",
  "Personalized Gifts",
  "Equipment Tags & Lamacoids",
  "Not sure yet",
] as const;